import {
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState
} from "react";
import { Button, Dropdown, MenuProps, Modal, Tooltip } from "antd";
import { message } from "utils/ciq-message";
import "../submittal-list.css";
import "./filter-bar.scss";
import { FilterChips } from "components";
import { ActionBar } from "components/navbar";
import CiqAgSearch from "components/ciq-ag-search";
import { FilterItem } from "models/types";
import {
  isPermissionNotGrantted,
  ProjectContext
} from "context/ProjectProvider";
import { MUTATION_UPDATE_SUBMITTALS } from "services/graphQL/mutations";
import { IntegrationProjectInfo } from "components/integration-project-info";
import ErrorBoundary from "components/error-boundary";
import ColumnConfigViews, {
  TSaveColumnState
} from "components/column-config-views";
import { CloseOutlined, DownOutlined } from "@ant-design/icons";
import ExportOptionsPopover, {
  ExportFormatOption,
  ExportSelection
} from "components/export-options-popover";
import { ExcelExportOptions } from "components/excel-ag-grid-export/excel-export-option";
import { useCIQMutation } from "hooks/ciq-gql-hooks";
import {
  DATE_FORMAT_MMDDYYYY_HHMM_A_Z,
  INPUT_DATE_FORMAT
} from "utils/dateutils";
import dayjs from "utils/dayjs-config";
import { useHistory } from "react-router";
import { CiqIconBox } from "components/svg-icons";
import { AgGridReact } from "ag-grid-react";
import {
  ErrorMessages,
  EFeaturePageId,
  ProjectPermissionEnum,
  SuccessMessages
} from "../../../constants";
import SubmittalUntrackedsTypeMenu from "pages/submittal-log-untracked/components/submittal-untrackeds-type-menu";
import { getSubmittalIntegrationConfig } from "pages/subscription-settings/integrations-tab-new/utils";
import {
  getSubmittalLogHeaderIconFeature,
  isSubmittalLogRestrictedView,
  isSubmittalLogSavedViewsEnabled,
  SubmittalLogView
} from "pages/submittal-log/submittal-log-view";

export type FilterProps = {
  gridRef: React.RefObject<AgGridReact<any>>;
  items: FilterItem[];
  onCreateLogClick: () => void;
  onImportLogClick: () => void;
  onAllSubmittalsClick: () => void;
  showFiterChips: boolean;
  onSearch: any;
  searchText?: string;
  stats: any;
  onExportCSV: () => void;
  onExportExcel: (options: ExcelExportOptions) => void;
  onOpenPrintView: () => void;
  selectedRow: Array<any>;
  onSubmittalEditClick: (
    panel:
      | "details"
      | "duration"
      | "deadline_date"
      | "extract_material"
      | "untracked"
  ) => void;
  isIntegrationMode: boolean;
  displayRowCount: number;
  isIntegrationConfigured: boolean;
  resetFilters: () => void;
  setFilterStateFromData: (columnState: TSaveColumnState) => void;
  afterApplyFilterCallBack?: (...args: any[]) => any;
  getColumnStateFromGrid: (props: { isIncludeSearchText?: boolean }) => {
    columnList: any;
    quickSearch: string;
  } | null;
  isGridDataRendered: boolean;
  handleDeleteSubmittal?: (submittalId: string[]) => void;
  logView?: SubmittalLogView;
  pageTitle?: string;
};

function SubmittalListFilterComponent(props: FilterProps) {
  const {
    gridRef,
    items,
    onCreateLogClick,
    onImportLogClick,
    onAllSubmittalsClick,
    showFiterChips,
    onSearch,
    searchText = "",
    stats,
    onExportCSV,
    onExportExcel,
    onOpenPrintView,
    selectedRow,
    onSubmittalEditClick,
    isIntegrationMode,
    displayRowCount,
    isIntegrationConfigured,
    resetFilters,
    setFilterStateFromData,
    getColumnStateFromGrid,
    afterApplyFilterCallBack,
    isGridDataRendered,
    handleDeleteSubmittal,
    logView = "default",
    pageTitle = "Submittals"
  } = props;

  const showSavedViews = isSubmittalLogSavedViewsEnabled(logView);
  const isRestrictedLogView = isSubmittalLogRestrictedView(logView);
  const headerIconFeature = getSubmittalLogHeaderIconFeature(logView);

  const selected = selectedRow ? selectedRow.length : 0;
  const history = useHistory();
  const {
    tokenContents,
    gqlClientForProject,
    projectIntegrationConfig,
    lastImportLogData,
    projectDetails,
    isFeatureFlagEnabled,
    isProcoreProject,
    isProjectArchived,
    columnHeaders: { submittalHeaderMap }
  } = useContext(ProjectContext);

  const isExportDisabled = !displayRowCount || !!isProjectArchived;
  const exportButtonTitle = isProjectArchived
    ? undefined
    : "Export Submittal Log";

  const isBulkLinkingEnabled = isFeatureFlagEnabled(
    "MATERIAL_MULTIPLE_LINKING"
  );

  const isGroupMaterialCreationEnabled = isFeatureFlagEnabled(
    "GROUPED_MATERIAL_CREATION"
  );

  const isPrintViewEnabled = isFeatureFlagEnabled("PRINT_VIEW");
  const isExcelExportEnabled = isFeatureFlagEnabled("EXCEL_EXPORT");

  const [updateSubmittals] = useCIQMutation(MUTATION_UPDATE_SUBMITTALS, {
    client: gqlClientForProject
  });

  const lastImportStatus = lastImportLogData?.status;

  const onChipDelete = (item: { field: string; value: string }) => {
    const filterModel = gridRef.current?.api.getFilterModel();
    const model = filterModel ? filterModel[item.field] : null;
    if (model) {
      const values = model.values.filter((val: any) => val !== item.value);
      if (values.length === 0) {
        gridRef.current?.api.destroyFilter(item.field);
        return;
      }
      gridRef.current?.api?.setColumnFilterModel(item.field, {
        ...model,
        values
      });
      gridRef.current?.api.onFilterChanged();
    }
  };

  const onCategoryDelete = (item: FilterItem) => {
    gridRef.current?.api.destroyFilter(item.field);
  };

  const cannotCreateSubmittal = isPermissionNotGrantted(
    ProjectPermissionEnum.CreateSubmittal,
    tokenContents?.role ?? ""
  );

  const deleteSubmittals = useCallback(() => {
    if (isRestrictedLogView) {
      Modal.confirm({
        title: "Are you sure you want to delete the selected submittal(s)?",
        okText: "Delete",
        cancelText: "Cancel",
        async onOk() {
          if (handleDeleteSubmittal && selectedRow.length > 0) {
            handleDeleteSubmittal(selectedRow.map((s) => s.id));
          }
        }
      });
      return;
    }

    if (isIntegrationMode) {
      // Count selected rows that have not_found_in_source === true
      const submittalsToDelete = selectedRow
        .filter((row: any) => row?.not_found_in_source === true)
        .map((row: any) => row.id);
      const presentSubmittalsCount =
        selectedRow.length - submittalsToDelete.length;
      Modal.confirm({
        title: "Are you sure you want to delete the selected submittal(s)?",
        content:
          presentSubmittalsCount > 0
            ? `${presentSubmittalsCount} of the ${selectedRow.length} selected submittals can't be deleted because they are still available in source system.`
            : null,
        okText: "Delete",
        cancelText: "Cancel",
        okButtonProps: {
          disabled: submittalsToDelete.length === 0
        },
        async onOk() {
          if (handleDeleteSubmittal && submittalsToDelete.length > 0) {
            handleDeleteSubmittal(submittalsToDelete);
          }
        }
      });
      return;
    }
    Modal.confirm({
      title: "Are you sure you want to delete the selected submittal(s)?",
      okText: "Yes",
      cancelText: "No",
      async onOk() {
        if (handleDeleteSubmittal) {
          handleDeleteSubmittal(selectedRow.map((s) => s.id));
          return;
        }
        const deletedSubmittalsRes = await updateSubmittals({
          variables: {
            set: { deleted: true },
            where: {
              id: { _in: selectedRow.map((s) => s.id) }
            }
          }
        });
        if (deletedSubmittalsRes.data) {
          message.success(SuccessMessages.deleteSuccess);
        }
        if (deletedSubmittalsRes.errors) {
          console.error(deletedSubmittalsRes.errors);
          message.error("Failed to delete Submittals.");
        }
      }
    });
  }, [
    handleDeleteSubmittal,
    isRestrictedLogView,
    isIntegrationMode,
    selectedRow,
    updateSubmittals
  ]);

  const dayjsUpdateIntervalRef = useRef<any>(null);
  // Seeded with now rather than null so canImportNewFile is a real answer on the
  // first render. Left null it evaluates to undefined until the interval's first
  // tick, which reads as "still on cooldown" — a disabled button and a cooldown
  // tooltip for a second on every mount, on projects that have no cooldown.
  const [dayjsInstance, setDayjsInstance] = useState<any>(() => dayjs());

  // The submittal integration, not projectIntegrationConfig[0] — an OPC schedule
  // config sitting in the same unordered array would otherwise answer for it.
  const submittalIntgConfig = useMemo(
    () => getSubmittalIntegrationConfig(projectIntegrationConfig),
    [projectIntegrationConfig]
  );

  // ?? null rather than leaving it undefined: .add(null) is a no-op +0, matching
  // the no-integration case, whereas .add(undefined) is NaN and poisons the whole
  // Dayjs into an Invalid Date.
  const importEnableTime =
    submittalIntgConfig?.systemIntgConfigProperties?.SYNC_INTERVAL ?? null;

  // .tz() shifts to dayjs.tz.setDefault(projectZone). Without it the Dayjs
  // sits in machine-local, and the "(z)" format token below falls back to
  // "GMT±offset" instead of the project-zone abbreviation (EDT, PDT…).
  const nextAllowedImportTime = dayjs(
    lastImportLogData?.lastSyncDateTime,
    INPUT_DATE_FORMAT
  )
    .tz()
    .add(importEnableTime, "minutes");

  const canImportNewFile = useMemo(() => {
    if (!lastImportLogData) return true;
    // An import log with no processed_at leaves nothing to count from, so there
    // is no cooldown to enforce. Guarding here matters because every comparison
    // against an Invalid Date is false: without it the button stays disabled for
    // good and the interval below never reaches its clear condition, re-rendering
    // the log once a second for as long as the page is open.
    if (!nextAllowedImportTime.isValid()) return true;
    return !!dayjsInstance?.isSameOrAfter(nextAllowedImportTime);
  }, [dayjsInstance, nextAllowedImportTime, lastImportLogData]);

  useEffect(() => {
    dayjsUpdateIntervalRef.current = setInterval(() => {
      setDayjsInstance(dayjs());
      if (canImportNewFile) {
        clearInterval(dayjsUpdateIntervalRef.current);
        dayjsUpdateIntervalRef.current = null;
      }
    }, 1000);
    return () => {
      if (dayjsUpdateIntervalRef.current) {
        clearInterval(dayjsUpdateIntervalRef.current);
      }
    };
  }, [canImportNewFile]);

  const syncBtnVisible = useMemo(() => {
    return (
      submittalIntgConfig?.systemIntgConfigProperties?.INTEGRATION_METHOD ===
      "api"
    );
  }, [submittalIntgConfig]);

  const isFileBasedIntegration = useMemo(() => {
    return (
      isIntegrationMode &&
      submittalIntgConfig?.systemIntgConfigProperties?.INTEGRATION_METHOD ===
        "file"
    );
  }, [isIntegrationMode, submittalIntgConfig]);

  const disabledMessage = useMemo(() => {
    if (isIntegrationMode && syncBtnVisible)
      return ErrorMessages.ActionDisabledDueToIntegrationMode;

    if (cannotCreateSubmittal) return ErrorMessages.PermissionNotGranted;

    return "";
  }, [cannotCreateSubmittal, isIntegrationMode, syncBtnVisible]);

  const hoverMsgForFileBasedImportBtn = useMemo(() => {
    if (isFileBasedIntegration) {
      if (lastImportStatus && lastImportStatus === "PROCESSING") {
        return "Import is in Progress.";
      }

      // isValid() before formatting: an unformattable cooldown is not a cooldown
      // to report, and "after Invalid Date" tells the user nothing they can act
      // on. canImportNewFile already lets the import through in that case.
      if (!canImportNewFile && nextAllowedImportTime.isValid()) {
        return `You can only import again after ${nextAllowedImportTime.format(
          DATE_FORMAT_MMDDYYYY_HHMM_A_Z
        )}`;
      }
      return "Import Log";
    }
    if (cannotCreateSubmittal) return ErrorMessages.PermissionNotGranted;
    return "";
  }, [
    isFileBasedIntegration,
    cannotCreateSubmittal,
    lastImportStatus,
    canImportNewFile,
    nextAllowedImportTime
  ]);

  const bulkEditActionMenu: MenuProps = useMemo(() => {
    return {
      items: [
        {
          type: "group",
          key: "bulk-edit",
          label: "Bulk Edit",
          children: [
            {
              key: 0,
              onClick: () => onSubmittalEditClick("details"),
              label: "Details"
            },
            {
              key: "deadline_date",
              onClick: () => onSubmittalEditClick("deadline_date"),
              label: submittalHeaderMap?.manual_entry_date || "Deadline Date"
            },
            {
              key: 1,
              onClick: () => onSubmittalEditClick("duration"),
              label: (
                <span data-testid="bulk-edit-durations-button">Durations</span>
              )
            },
            isBulkLinkingEnabled
              ? {
                  key: "link",
                  onClick: () => {
                    history.push(
                      `/project/${projectDetails?.id}/schedule/submittal-linking`,
                      {
                        name: "Bulk Edit Linking",
                        selectedRows: selectedRow
                      }
                    );
                  },
                  label: "Links"
                }
              : null
          ]
        },
        { type: "divider" },
        isGroupMaterialCreationEnabled && !cannotCreateSubmittal
          ? {
              key: "extract_material",
              onClick: () => onSubmittalEditClick("extract_material"),
              label: (
                <span data-testid="bulk-extract-materials-button">
                  Create Materials
                </span>
              )
            }
          : null,

        {
          key: "untracked",
          onClick: () => onSubmittalEditClick("untracked"),
          label: (
            <span data-testid="bulk-edit-untracked-button">Do not track</span>
          )
        }
      ]
    };
  }, [
    history,
    isBulkLinkingEnabled,
    isGroupMaterialCreationEnabled,
    cannotCreateSubmittal,
    onSubmittalEditClick,
    projectDetails?.id,
    selectedRow,
    submittalHeaderMap?.manual_entry_date
  ]);

  const bulkEditButton = useMemo(() => {
    if (selected < 1 || isRestrictedLogView) return null;

    return (
      <div className="bg-ciq-gray-100 bg-opacity-70 py-1 px-4 flex items-center space-x-2 rounded-md">
        <div className="py-1">{selected} Selected</div>
        {isGroupMaterialCreationEnabled &&
          !cannotCreateSubmittal &&
          selected === 1 && (
            <Button
              onClick={() => {
                onSubmittalEditClick("extract_material");
              }}
            >
              Create Materials
            </Button>
          )}
        {selected > 1 && (
          <Dropdown menu={bulkEditActionMenu} disabled={cannotCreateSubmittal}>
            <Button
              disabled={cannotCreateSubmittal}
              title={
                cannotCreateSubmittal
                  ? ErrorMessages.PermissionNotGranted
                  : "Actions"
              }
              data-testid="bulk-edit-button"
            >
              Actions <DownOutlined />
            </Button>
          </Dropdown>
        )}
        {!isProcoreProject && (
          <Button
            onClick={deleteSubmittals}
            size="middle"
            className="bottomBarBtn"
            title="Delete"
            disabled={cannotCreateSubmittal}
            data-testid="delete-button"
          >
            Delete
          </Button>
        )}
      </div>
    );
  }, [
    bulkEditActionMenu,
    cannotCreateSubmittal,
    deleteSubmittals,
    isRestrictedLogView,
    isGroupMaterialCreationEnabled,
    isProcoreProject,
    onSubmittalEditClick,
    selected
  ]);

  const disableAccImportLogBtn = useMemo(() => {
    return (
      cannotCreateSubmittal ||
      (lastImportStatus && lastImportStatus === "PROCESSING") ||
      !canImportNewFile
    );
  }, [canImportNewFile, cannotCreateSubmittal, lastImportStatus]);

  const addSubmittalMenu: MenuProps = useMemo(
    () => ({
      className: "w-[330px]",
      items: [
        {
          key: "create_new",
          onClick: () => onCreateLogClick(),
          disabled: isIntegrationMode || cannotCreateSubmittal,
          className: "px-4 py-2",
          title: cannotCreateSubmittal
            ? disabledMessage
            : "Create Individually",
          label: (
            <div className="flex flex-col space-y-0.5">
              <div className="font-medium text-black text-opacity-90">
                Create Individually
              </div>
              <div className="text-ciq-gray-900 text-xs">
                Create a single Submittal manually
              </div>
            </div>
          )
        },
        {
          key: "import_log",
          onClick: () => onImportLogClick(),
          className: "px-4 py-2",
          disabled: isIntegrationMode || cannotCreateSubmittal,
          title: cannotCreateSubmittal ? disabledMessage : "Import Submittals",
          label: (
            <div className="flex flex-col space-y-0.5">
              <div className="font-medium text-black text-opacity-90">
                Import Submittals
              </div>
              <div className="text-ciq-gray-900 text-xs">
                Upload a CSV to add or update Submittals
              </div>
            </div>
          )
        }
      ]
    }),
    [
      cannotCreateSubmittal,
      disabledMessage,
      isIntegrationMode,
      onCreateLogClick,
      onImportLogClick
    ]
  );

  const exportOptions: ExportFormatOption[] = [
    {
      key: "export_csv",
      title: "CSV",
      testId: "export-csv-button"
    }
  ];
  if (!isRestrictedLogView && isExcelExportEnabled) {
    exportOptions.push({
      key: "export_excel",
      title: "Excel (.xlsx)",
      isExcel: true,
      testId: "export-excel-button"
    });
  }
  if (!isRestrictedLogView && isPrintViewEnabled) {
    exportOptions.push({
      key: "print_view",
      title: "PDF/Print",
      testId: "submittal-header-print-view"
    });
  }

  const onProceedExport = ({ key, excelOptions }: ExportSelection) => {
    if (key === "export_excel") {
      if (excelOptions) onExportExcel(excelOptions);
      return;
    }
    if (key === "print_view") {
      onOpenPrintView();
      return;
    }
    onExportCSV();
  };

  return (
    <ActionBar>
      <div className="flex min-w-0 flex-1 items-center space-x-3">
        <CiqIconBox feature={headerIconFeature} size={42} padding={0} />
        <div className="flex flex-col min-w-0 flex-1">
          <div className="flex space-x-2 text-black">
            <Dropdown
              menu={SubmittalUntrackedsTypeMenu()}
              trigger={["hover"]}
              placement="bottomLeft"
            >
              <div className="flex items-center gap-2">
                <span className="text-black text-xl font-semibold">
                  {pageTitle}
                </span>
                <DownOutlined className="text-xs self-center mt-0.5" />
              </div>
            </Dropdown>

            {(() => {
              if (!stats || stats === "") {
                return null;
              }
              if (stats.props) {
                return (
                  <>
                    <div
                      className="flex flex-wrap items-center border border-solid border-ciq-gray-300 rounded-xl px-2 text-black text-opacity-50 text-xs font-normal"
                      data-testid="log-items-count"
                    >
                      {stats.props.children[0]}
                    </div>
                    {!isRestrictedLogView && (
                      <Tooltip title="Remove to view all Submittals">
                        <div className="flex flex-wrap items-center border border-solid rounded-xl px-1 text-xs font-normal bg-ciq-blue-100 text-ciq-blue-700 border-ciq-blue-300">
                          {stats.props.children[1]}
                          <CloseOutlined
                            className="mt-[3px] text-xs text-ciq-blue-700"
                            style={{ fontSize: 10 }}
                            onClick={() => {
                              onAllSubmittalsClick();
                            }}
                            data-testid="button-clear-filter"
                          />
                        </div>
                      </Tooltip>
                    )}
                  </>
                );
              }
              return (
                <div
                  className="flex flex-wrap items-center border border-solid border-ciq-gray-300 rounded-xl px-2 text-black text-opacity-50 text-xs font-normal"
                  data-testid="log-items-count"
                >
                  {stats}
                </div>
              );
            })()}
          </div>
          {isIntegrationMode &&
            isIntegrationConfigured &&
            logView === "default" && (
              <div className="flex h-6 items-center">
                <ErrorBoundary>
                  <IntegrationProjectInfo
                    syncBtnVisible={syncBtnVisible}
                    isFileBasedIntegration={isFileBasedIntegration}
                  />
                </ErrorBoundary>
              </div>
            )}
        </div>
      </div>

      <div className="flex space-x-2 items-center">
        {bulkEditButton}
        {showFiterChips && (
          <FilterChips
            items={items}
            onChipDelete={onChipDelete}
            onCategoryDelete={onCategoryDelete}
            resetAll={resetFilters}
          />
        )}
        <div className="flex">
          <CiqAgSearch
            gridRef={gridRef}
            placeholder="Search Submittals"
            value={searchText}
            onSearch={onSearch}
            disabled={isGridDataRendered}
            data-testid="input-search-submittals"
          />
        </div>
        {showSavedViews && (
          <ColumnConfigViews
            setFilterStateFromData={setFilterStateFromData}
            getColumnStateFromGrid={() =>
              getColumnStateFromGrid({ isIncludeSearchText: false })
            }
            afterApplyFilterCallBack={afterApplyFilterCallBack}
            featureId={EFeaturePageId.SUBMITTAL}
          />
        )}

        {isIntegrationMode &&
          isFileBasedIntegration &&
          !isRestrictedLogView && (
            <Tooltip
              title={hoverMsgForFileBasedImportBtn}
              styles={{
                container: {
                  backgroundColor: "white",
                  opacity: 0.88
                }
              }}
            >
              <Button
                disabled={disableAccImportLogBtn}
                onClick={() => onImportLogClick()}
                data-testid="integration-import-log-button"
              >
                Import Log
              </Button>
            </Tooltip>
          )}

        <ExportOptionsPopover
          options={exportOptions}
          onProceed={onProceedExport}
          excelTableName={projectDetails?.name || "Project"}
          showIncludeComments={false}
          disabled={isExportDisabled}
          buttonType="default"
          buttonTestId="export-button"
          buttonTitle={exportButtonTitle}
        />

        {!isIntegrationMode && !isRestrictedLogView && (
          <Dropdown
            key="SubmittalLogPage_addSubmittal"
            menu={addSubmittalMenu}
            trigger={["click", "hover"]}
            disabled={cannotCreateSubmittal}
          >
            <Button
              type="primary"
              disabled={isIntegrationMode || cannotCreateSubmittal}
              title={cannotCreateSubmittal ? disabledMessage : "Add Submittal"}
              data-testid="new-submittal-button"
            >
              Add Submittal <DownOutlined />
            </Button>
          </Dropdown>
        )}
      </div>
    </ActionBar>
  );
}

export default SubmittalListFilterComponent;
